/**
 * Avatar Initials Generation Utility
 *
 * Generates deterministic two-character uppercase initials from any name input.
 * Follows the specification defined in BNEP-675.
 *
 * @see docs/BNEP-675-AVATAR-INITIALS-GENERATION.md
 */

/** Fallback value when no alphabetic characters are found */
export const FALLBACK_INITIALS = '??'

/** Regex to split on word separators: space, hyphen, underscore, dot */
const WORD_SEPARATOR_REGEX = /[\s\-_.]+/

/**
 * Regex to match Unicode letters.
 * Using RegExp constructor to support Unicode property escapes with ES5 target.
 */
// eslint-disable-next-line prefer-regex-literals
const LETTER_REGEX = new RegExp('\\p{L}', 'u')

/**
 * Checks if a character is a Unicode letter.
 * Supports international characters (Latin, Chinese, Japanese, Arabic, etc.)
 */
const isLetter = (char: string): boolean => LETTER_REGEX.test(char)

/**
 * Extracts only letter characters from a string, ignoring numbers and symbols.
 */
const extractLetters = (str: string): string => str.split('').filter(isLetter).join('')

/**
 * Generates avatar initials from a string.
 *
 * Algorithm (priority order):
 * 1. Multi-word: first letter of first word + first letter of last word
 * 2. Single-word (>=2 chars): first two letters
 * 3. Single-word (1 char): duplicate the character
 * 4. Fallback: "??" if no alphabetic characters found
 *
 * Normalization rules:
 * - Trims leading/trailing whitespace
 * - Treats spaces, hyphens, underscores, dots as word separators
 * - Ignores non-alphabetic characters (numbers, punctuation, emojis)
 * - Preserves Unicode letters (e.g., E, A, N)
 * - Returns uppercase output
 *
 * @example
 * generateAvatarInitials('Jane Smith')       // 'JS'
 * generateAvatarInitials('Jean-Luc Picard')  // 'JP'
 * generateAvatarInitials('Google')           // 'GO'
 * generateAvatarInitials('X')                // 'XX'
 * generateAvatarInitials('#1 Support')       // 'SU'
 * generateAvatarInitials('12345')            // '??'
 */
export const generateAvatarInitials = (input: string): string => {
  const trimmed = input.trim()
  if (!trimmed) return FALLBACK_INITIALS

  // Split by word separators, extract only letters from each word, filter empty
  const words = trimmed.split(WORD_SEPARATOR_REGEX).map(extractLetters).filter(Boolean)

  const wordCount = words.length

  // No alphabetic characters found
  if (wordCount === 0) return FALLBACK_INITIALS

  // Multi-word: first letter of first word + first letter of last word
  if (wordCount >= 2) {
    return (words[0][0] + words[wordCount - 1][0]).toUpperCase()
  }

  // Single word
  const word = words[0]
  return (word.length >= 2 ? word[0] + word[1] : word[0] + word[0]).toUpperCase()
}

/**
 * Extracts the local part of an email address (before the @ symbol).
 * Returns the original string if no @ is found.
 *
 * @example
 * extractEmailLocalPart('john.smith@company.com') // 'john.smith'
 * extractEmailLocalPart('user@test.org')          // 'user'
 * extractEmailLocalPart('notanemail')             // 'notanemail'
 */
const extractEmailLocalPart = (email: string): string => {
  const atIndex = email.indexOf('@')
  return atIndex > 0 ? email.substring(0, atIndex) : email
}

/**
 * Convenience function with fallback chain.
 * Tries accountName first, then email (local part only), then returns fallback.
 *
 * For emails, extracts the local part before @ to avoid TLD affecting initials.
 *
 * @example
 * getAvatarInitials({ accountName: 'Acme Corp' })                    // 'AC'
 * getAvatarInitials({ email: 'john.smith@company.com' })             // 'JS'
 * getAvatarInitials({ email: 'user@test.com' })                      // 'US'
 * getAvatarInitials({ accountName: 'Acme', email: 'user@test.com' }) // 'AC'
 * getAvatarInitials({})                                               // '??'
 */
export const getAvatarInitials = (params: { accountName?: string; email?: string }): string => {
  const { accountName, email } = params

  if (accountName) {
    return generateAvatarInitials(accountName)
  }

  if (email) {
    return generateAvatarInitials(extractEmailLocalPart(email))
  }

  return FALLBACK_INITIALS
}
